/**
 * NDA Subject helpers — single source of truth for the auto-fill behaviour
 * used by both the New NDA dialog (`pages/NDAs.jsx`) and the Draft NDA
 * edit surface (`pages/NDADetail.jsx`).
 *
 * 2026-02-25 — introduced when the auto-generation was extended from the
 * New NDA form to the Detail edit form so the two surfaces cannot drift
 * (spec: "Prefer extracting/reusing one common buildDefaultNdaSubject()").
 *
 * Format contract (byte-exact — the separator is EN DASH U+2013):
 *   Data NDA     → "Evaluation of REEDOS Data – {Party}"
 *   Software NDA → "Evaluation of REEDOS Software – {Party}"
 *
 * Callers treat an empty return value as "cannot auto-generate yet".
 * Never mutates its arguments.
 */

/**
 * Build the default NDA Subject for a given (ndaType, partyName) pair.
 *
 * @param {string} ndaType    "Data" | "Software"
 * @param {string} partyName  Free-form party or client display name
 * @returns {string}          The auto-generated subject or "" if either
 *                            argument is missing.
 */
export function buildDefaultNdaSubject(ndaType, partyName) {
    const type = (ndaType || "").trim();
    const party = (partyName || "").trim();
    if (!type || !party) return "";
    if (type === "Data") return `Evaluation of REEDOS Data – ${party}`;
    if (type === "Software") return `Evaluation of REEDOS Software – ${party}`;
    return "";
}

/**
 * Extract the party's display name from a persisted NDA (`party_snapshot`)
 * so the Detail edit form can compute the auto-default for a Draft NDA.
 * Falls back through display_name → party_name → "" so the helper stays
 * defensive across historical records.
 *
 * @param {object|null} nda   The persisted NDA record from the backend
 * @returns {string}          Party name, or "" if unavailable
 */
export function partyDisplayNameFromNda(nda) {
    if (!nda) return "";
    const ps = nda.party_snapshot || {};
    return (
        (ps.party_display_name || ps.party_name || ps.legal_name || "").trim()
    );
}

/**
 * Compute the auto-default Subject for an existing NDA record.
 * Convenience wrapper — used by the Detail edit input's clear-to-regenerate
 * hook.
 *
 * @param {object|null} nda   The persisted NDA record from the backend
 * @returns {string}          Auto-generated subject, or "" when either
 *                            NDA type or party name is unavailable.
 */
export function defaultSubjectForNda(nda) {
    if (!nda) return "";
    return buildDefaultNdaSubject(nda.nda_type, partyDisplayNameFromNda(nda));
}
